import { useGetList, useTranslate } from "ra-core";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SplitFlap } from "../misc/SplitFlap";
import type { Deal } from "../types";
import { getUserLocale, sumReportingAmount } from "../misc/currency";

const ORANGE = "var(--nd-orange)";

interface MetricProps {
  value: string;
  label: string;
  info?: string;
  orange?: boolean;
  border?: boolean;
  startDelay?: number;
  compact?: boolean;
}

const Metric = ({
  value,
  label,
  info,
  orange = false,
  border = false,
  startDelay = 0,
  compact = false,
}: MetricProps) => (
  <div
    className={`flex flex-col gap-2 ${compact ? "py-4" : "py-6"}`}
    style={
      border
        ? {
            paddingLeft: compact ? "1.25rem" : "2rem",
            paddingRight: compact ? "1.25rem" : "2rem",
            borderLeft: "1px solid var(--nd-border)",
          }
        : { paddingRight: compact ? "1.25rem" : "2rem" }
    }
  >
    <SplitFlap
      value={value}
      startDelay={startDelay}
      className={`font-nd-display leading-none font-thin tabular-nums ${compact ? "text-[2.25rem]" : "text-[3.5rem]"}`}
      style={{ color: orange ? ORANGE : "var(--nd-text-display)" }}
    />
    <div className="flex items-center gap-1.5">
      <span className="font-nd-mono text-[10px] tracking-[0.1em] uppercase text-nd-text-disabled">
        {label}
      </span>
      {info && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 text-nd-text-disabled hover:text-nd-text-secondary"
            >
              <Info className="w-3 h-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-3"
            style={{ fontFamily: "var(--font-nd-mono)", fontSize: "11px" }}
          >
            {info}
          </PopoverContent>
        </Popover>
      )}
    </div>
  </div>
);

export const DashboardMetrics = ({
  totalContacts,
  compact = false,
}: {
  totalContacts: number;
  compact?: boolean;
}) => {
  const { currency } = useConfigurationContext();
  const translate = useTranslate();

  const userLocale = getUserLocale();

  const {
    data: deals,
    isPending,
    isError,
  } = useGetList<Deal>("deals", {
    // 1000 covers most real-world datasets; true aggregate accuracy requires a DB view
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_at", order: "DESC" },
    filter: { "stage@neq": "lost" },
  });

  const pipelineValue =
    isPending || isError ? null : sumReportingAmount(deals ?? []);

  const openDeals =
    isPending || isError
      ? null
      : (deals?.filter((d) => !["won", "lost"].includes(d.stage)).length ?? 0);

  const formatPipeline = (v: number) =>
    new Intl.NumberFormat(userLocale, {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: v >= 1_000_000 ? 1 : 0,
    }).format(v);

  return (
    <div className="flex">
      <Metric
        value={pipelineValue !== null ? formatPipeline(pipelineValue) : "—"}
        label={translate("crm.dashboard.metrics.pipeline", { _: "Pipeline" })}
        info={translate("crm.dashboard.metrics.pipeline_info", {
          _: "Total value of all active and won deals — excludes Lost.",
        })}
        orange
        startDelay={0}
        compact={compact}
      />
      <Metric
        value={openDeals !== null ? String(openDeals) : "—"}
        label={translate("crm.dashboard.metrics.open_deals", {
          _: "Open Deals",
        })}
        info={translate("crm.dashboard.metrics.open_deals_info", {
          _: "Deals in active stages — excludes Won and Lost.",
        })}
        border
        startDelay={150}
        compact={compact}
      />
      <Metric
        value={totalContacts ? String(totalContacts) : "—"}
        label={translate("resources.contacts.name", {
          smart_count: 2,
          _: "Contacts",
        })}
        info={translate("crm.dashboard.metrics.contacts_info", {
          _: "Total contacts in your CRM.",
        })}
        border
        startDelay={300}
        compact={compact}
      />
    </div>
  );
};
